import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { useGamificationStore, type Badge } from '@/store/gamificationStore';

export default function BadgesPage() {
  const { badges, level, fetchBadges, fetchLevel, checkBadges } = useGamificationStore();
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [filter, setFilter] = useState<string>('all');

  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => api.get('/gamification/leaderboard').then((r) => r.data),
  });

  useEffect(() => {
    fetchBadges();
    fetchLevel();
    // Check for new badges on mount
    checkBadges().then((nb) => setNewBadges(nb));
  }, []);

  const categories = ['all', ...new Set(badges.map((b) => b.category))];
  const filtered = filter === 'all' ? badges : badges.filter((b) => b.category === filter);
  const earnedCount = badges.filter((b) => b.earned).length;

  return (
    <>
      <Helmet><title>Badge & Livelli — React-City</title></Helmet>

      <div className="space-y-8 animate-in">
        {/* New badges toast */}
        {newBadges.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg">
            <span className="text-3xl">🎉</span>
            <div>
              <p className="font-bold">Nuovi badge sbloccati!</p>
              <p className="text-sm opacity-90">
                {newBadges.map((b) => `${b.icon} ${b.name}`).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/* Level card */}
        {level && (
          <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-surface-900 dark:text-white">
                  Livello {level.level} — {level.level_name}
                </h2>
                <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">
                  {level.total_xp} XP totali · Posizione #{level.rank ?? '?'}
                </p>
              </div>
              <div className="text-4xl">
                {level.level >= 8 ? '👑' : level.level >= 5 ? '⭐' : '�️'}
              </div>
            </div>

            {/* XP Progress bar */}
            <div className="relative h-4 bg-surface-100 dark:bg-surface-800 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all duration-500"
                style={{ width: `${level.progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-surface-400">
              <span>{level.total_xp} XP</span>
              <span>{level.next_level_xp} XP</span>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white dark:bg-surface-900 rounded-2xl p-4 border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{earnedCount}</p>
            <p className="text-xs text-surface-500 mt-1">Badge ottenuti</p>
          </div>
          <div className="bg-white dark:bg-surface-900 rounded-2xl p-4 border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-surface-900 dark:text-white">{badges.length}</p>
            <p className="text-xs text-surface-500 mt-1">Totale disponibili</p>
          </div>
          <div className="bg-white dark:bg-surface-900 rounded-2xl p-4 border border-surface-200 dark:border-surface-700 text-center">
            <p className="text-2xl font-bold text-emerald-500">
              {badges.length > 0 ? Math.round((earnedCount / badges.length) * 100) : 0}%
            </p>
            <p className="text-xs text-surface-500 mt-1">Completamento</p>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition-colors ${
                filter === cat
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400'
                  : 'text-surface-500 hover:bg-surface-50 dark:hover:bg-surface-800'
              }`}
            >
              {cat === 'all' ? 'Tutti' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Badge grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((badge) => (
            <div
              key={badge.badge_id}
              className={`relative rounded-2xl p-4 border text-center transition-all ${
                badge.earned
                  ? 'bg-white dark:bg-surface-900 border-primary-200 dark:border-primary-800 shadow-sm'
                  : 'bg-surface-50 dark:bg-surface-900/50 border-surface-200 dark:border-surface-800 opacity-60'
              }`}
            >
              <div className={`text-4xl mb-2 ${badge.earned ? '' : 'grayscale'}`}>
                {badge.icon}
              </div>
              <h3 className={`text-sm font-semibold ${badge.earned ? 'text-surface-900 dark:text-white' : 'text-surface-400'}`}>
                {badge.name}
              </h3>
              <p className="text-[10px] text-surface-400 mt-1 line-clamp-2">
                {badge.description}
              </p>
              <div className="mt-2 flex items-center justify-center gap-1">
                <span className="text-[10px] font-medium text-primary-500">+{badge.xp_reward} XP</span>
              </div>
              {badge.earned && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Leaderboard */}
        {leaderboard && (
          <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-100 dark:border-surface-800">
              <h3 className="font-semibold text-surface-900 dark:text-white">🏅 Leaderboard</h3>
            </div>
            <div className="divide-y divide-surface-100 dark:divide-surface-800">
              {(leaderboard.leaderboard ?? []).slice(0, 10).map((entry: any) => (
                <div key={entry.user_id} className="px-4 py-2 flex items-center gap-3">
                  <span className={`w-6 text-center text-sm font-bold ${
                    entry.rank === 1 ? 'text-yellow-500' : entry.rank === 2 ? 'text-gray-400' : entry.rank === 3 ? 'text-amber-600' : 'text-surface-400'
                  }`}>
                    {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-surface-900 dark:text-white">{entry.full_name}</p>
                    <p className="text-[10px] text-surface-400">Lv.{entry.level} {entry.level_name} · {entry.badge_count} badge</p>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    {entry.total_xp} XP
                  </span>
                </div>
              ))}
            </div>
            {leaderboard.your_rank && (
              <div className="px-4 py-2 bg-primary-50/50 dark:bg-primary-500/5 text-sm text-primary-700 dark:text-primary-400">
                La tua posizione: #{leaderboard.your_rank}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
